import React, { useReducer } from 'react';
import Grid from '@mui/material/Grid';
import { Button } from '@mui/material';
import { getValidDeviceTypes } from 'shared/utils/formatters';
import { createMac } from 'features/tenant/api/macs';
import { useRequest } from 'shared/hooks';
import { VARIANTS } from 'shared/constants';
import { TextEditor, withDataValidator, withStaticSelectEditor } from 'shared/components/data-display/editors';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';
import { deviceTypeWired } from './helper';

const initialDeviceState = {
  deviceName: '',
  deviceType: deviceTypeWired,
  mac: '',
};

/**
 * @param {Object} props
 * @param {string} props.reason - Reason why devices cannot be added
 */
function DisallowReason({ reason }) {
  return <strong>{`Can't add devices: ${reason}.`}</strong>;
}

/**
 * @param {Object} props
 * @param {Function} props.afterCreate - Callback function to execute after device creation
 * @param {Object} props.dot1x - Dot1x configuration object
 * @param {Object} props.tenantState - Tenant state object
 */
export default function DeviceForm(props) {
  const {
    afterCreate,
    dot1x,
    tenantState,
  } = props;

  const { request, isLoading } = useRequest();
  const [state, dispatch] = useReducer(dataReducer, {
    ...initialDataReducerState,
    data: initialDeviceState,
  });

  const { data, dirty, updatedData } = state;

  if (!tenantState.building || !tenantState.building.unit) {
    return <DisallowReason reason="tenant is archived" />;
  }
  if (!dot1x?.nas_profile_id) {
    return <DisallowReason reason="Building is not setup for dot1x" />;
  }

  const {
    disallowReason,
    ethernetEnabled,
  } = getValidDeviceTypes(tenantState.building, tenantState.building.unit);
  if (disallowReason) {
    return <DisallowReason reason={disallowReason} />;
  }

  const {
    data: { id: tenantId },
  } = tenantState;

  const Submit = async () => {
    const { deviceName, mac } = updatedData;

    await request(
      createMac(tenantId, mac, deviceName),
      `Created wired device: "${deviceName}"`,
    ).then((result) => {
      // Only reset if request was successful
      if (result) {
        dispatch({ action: DataReducerActions.clear });
        afterCreate(result);
      }
    });
  };

  return (
    <>
      <Grid
        container
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Grid size={{ xs: 12, sm: 3 }}>
          {/* Only WIRED is offered; the selector stays because supported types change often. */}
          <DataFormatter
            descriptions={[{
              key: 'deviceType',
              label: 'Device Type',
              editor: withStaticSelectEditor([deviceTypeWired]),
            }]}
            data={data}
            editing
            disabled={!ethernetEnabled}
            updatedData={updatedData}
            updateValue={(key, value) => dispatch({
              action: DataReducerActions.update,
              payload: { key, value },
            })}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <DataFormatter
            descriptions={[{
              key: 'deviceName',
              label: 'Device Name',
              editor: TextEditor,
            }]}
            data={data}
            editing
            disabled={!ethernetEnabled}
            updatedData={updatedData}
            updateValue={(key, value) => dispatch({
              action: DataReducerActions.update,
              payload: { key, value },
            })}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <DataFormatter
            descriptions={[{
              key: 'mac',
              label: 'Mac',
              editor: TextEditor,
              placeholder: 'DE:AD:BE:EF:12:34',
              validator: withDataValidator(
                {
                  validatorFunc: (value) => /(?:[a-f0-9]{2}:?){5}[a-f0-9]{2}/i.test(value),
                  helperText: 'Not a valid MAC Address',
                },
              ),
            }]}
            data={data}
            editing
            disabled={!ethernetEnabled}
            updatedData={updatedData}
            updateValue={(key, value) => dispatch({
              action: DataReducerActions.update,
              payload: { key, value },
            })}
          />
        </Grid>
      </Grid>
      <Grid
        container
        size={12}
        sx={{
          justifyContent: 'end',
        }}
      >
        <Grid sx={{
          mr: 2,
        }}
        >
          <Button
            sx={{ visibility: dirty ? 'visible' : 'hidden' }}
            disabled={!dirty}
            variant={VARIANTS.outlined}
            onClick={() => dispatch({ action: DataReducerActions.clear })}
          >
            Clear
          </Button>
        </Grid>
        <Grid>
          <Button
            onClick={Submit}
            disabled={isLoading || !updatedData?.mac || !updatedData?.deviceName}
            type="submit"
            variant={VARIANTS.contained}
          >
            Create
          </Button>
        </Grid>
      </Grid>
    </>
  );
}
